import * as LucideIcons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SectionNavLink, ReportCategory } from "../types";

// Sidebar nav stays a FLAT array (so the RBAC flattening in pageAccess.server.ts
// is untouched). The optional `group` field is read only by the sidebar render
// to draw collapsible sections — items sharing a `group` must stay contiguous.
export const sidebarLinks = [
  {
    imgURL: "/icons/home.svg",
    route: "/",
    baseRoute: "/",
    label: "Home",
    icon: LucideIcons.LayoutDashboard,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    imgURL: "/icons/home.svg",
    route: "/class/attendance",
    baseRoute: "/class",
    label: "Classes",
    icon: LucideIcons.BookOpenText,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },

  // ── Calendar ──────────────────────────────────────────────
  {
    imgURL: "/icons/users.svg",
    route: "/calendar",
    baseRoute: "/calendar",
    label: "Calendar",
    icon: LucideIcons.Calendar,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    group: "Calendar",
  },
  {
    imgURL: "/icons/users.svg",
    route: "/calendar?tab=holidays",
    baseRoute: "/calendar",
    label: "Holidays",
    icon: LucideIcons.Palmtree,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    group: "Calendar",
  },
  {
    imgURL: "/icons/users.svg",
    route: "/events",
    baseRoute: "/events",
    label: "Events",
    icon: LucideIcons.CalendarDays,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    group: "Calendar",
  },
  {
    imgURL: "/icons/users.svg",
    route: "/event-proposals",
    baseRoute: "/event-proposals",
    label: "Event Requests",
    icon: LucideIcons.ClipboardPenLine,
    visible: ["superadmin", "masteradmin", "principal"],
    group: "Calendar",
  },
  {
    imgURL: "/icons/users.svg",
    route: "/my-invitations",
    baseRoute: "/my-invitations",
    label: "My Invitations",
    icon: LucideIcons.MailQuestion,
    visible: ["teacher"],
    group: "Calendar",
  },
  {
    imgURL: "/icons/users.svg",
    route: "/event-design-requests",
    baseRoute: "/event-design-requests",
    label: "Design Requests",
    icon: LucideIcons.Paintbrush,
    visible: ["superadmin", "masteradmin"],
    group: "Calendar",
  },
  {
    route: "/open-day",
    baseRoute: "/open-day",
    label: "Open Day",
    icon: LucideIcons.DoorOpen,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    group: "Calendar",
  },

  // ── Students ──────────────────────────────────────────────
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/deusers",
    route: "/deusers/enrolment",
    label: "DeUsers",
    icon: LucideIcons.User2,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    group: "Students",
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/video_due",
    route: "/video_due",
    label: "Video Due",
    icon: LucideIcons.Video,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    group: "Students",
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/lead",
    route: "/lead",
    label: "Leads",
    icon: LucideIcons.UserPlus,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    group: "Students",
  },

  // ── Centre ────────────────────────────────────────────────
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/centre-evaluations",
    route: "/centre-evaluations",
    label: "Evaluations",
    icon: LucideIcons.ClipboardCheck,
    visible: ["superadmin", "masteradmin", "principal"],
    group: "Centre",
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/merchandise",
    route: "/merchandise",
    label: "Merchandise",
    icon: LucideIcons.Gift,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    group: "Centre",
  },

  // ── standalone ────────────────────────────────────────────
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/certificate",
    route: "/certificate/ready",
    label: "Certificate",
    icon: LucideIcons.Trophy,
    visible: ["superadmin", "masteradmin"],
  },
  {
    imgURL: "/icons/users.svg",
    // No baseRoute: each section below is a sibling page, so a prefix match
    // here would keep Overview highlighted on every one of them.
    route: "/teacher-qualifications",
    label: "Overview",
    icon: LucideIcons.LayoutDashboard,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    // Keep the module reachable by direct URL while it is introduced in
    // phases. This only hides navigation; role and page-access rules remain.
    sidebarVisible: false,
    group: "Training & Examination",
  },
  {
    baseRoute: "/teacher-qualifications/teacher",
    route: "/teacher-qualifications/teacher",
    label: "Teacher",
    icon: LucideIcons.UsersRound,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
    sidebarVisible: false,
    group: "Training & Examination",
  },
  {
    baseRoute: "/teacher-qualifications/reports",
    route: "/teacher-qualifications/reports",
    label: "Yearly Reports",
    icon: LucideIcons.BarChart3,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    sidebarVisible: false,
    group: "Training & Examination",
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/report",
    route: "/report",
    label: "Report",
    icon: LucideIcons.ChartBar,
    visible: ["superadmin", "masteradmin", "principal"],
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/analysis",
    route: "/analysis",
    label: "Analysis",
    icon: LucideIcons.LineChart,
    visible: ["superadmin", "masteradmin", "principal"],
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/ticket",
    route: "/ticket",
    label: "Ticket",
    icon: LucideIcons.Ticket,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/video-library",
    route: "/video-library",
    label: "Video Library",
    icon: LucideIcons.Clapperboard,
    // Kept out of the sidebar while the library is being built; the pages stay
    // reachable by URL. NEXT_PUBLIC_VIDEO_LIBRARY_NAV=true puts it back.
    visible:
      process.env.NEXT_PUBLIC_VIDEO_LIBRARY_NAV === "true"
        ? ["superadmin"]
        : [],
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/resources",
    route: "/resources",
    label: "Databank",
    icon: LucideIcons.FolderOpen,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
  {
    imgURL: "/icons/users.svg",
    baseRoute: "/notifications",
    route: "/notifications",
    label: "Notifications",
    icon: LucideIcons.Bell,
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },

  // ── Control Panel ─────────────────────────────────────────
  {
    imgURL: "/icons/home.svg",
    route: "/branch",
    baseRoute: "/branch",
    label: "Branch",
    icon: LucideIcons.Home,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    group: "Control Panel",
  },
  {
    imgURL: "/icons/home.svg",
    baseRoute: "/users",
    route: "/users/teacher",
    label: "Users",
    icon: LucideIcons.Users,
    visible: ["superadmin", "masteradmin", "principal", "manager"],
    group: "Control Panel",
  },
  {
    imgURL: "/icons/home.svg",
    baseRoute: "/promocode",
    route: "/promocode/active",
    label: "Promo Code",
    icon: LucideIcons.Ticket,
    visible: ["superadmin", "masteradmin", "principal"],
    group: "Control Panel",
  },
  {
    imgURL: "/icons/home.svg",
    baseRoute: "/structure",
    route: "/structure/category",
    label: "Structure",
    icon: LucideIcons.Layers,
    visible: ["superadmin", "masteradmin", "principal"],
    group: "Control Panel",
  },
];

// Header icon/label for each collapsible sidebar group (render-only metadata).
export const sidebarGroupMeta: Record<
  string,
  { label: string; icon: LucideIcons.LucideIcon }
> = {
  Calendar: { label: "Calendar", icon: LucideIcons.CalendarDays },
  Students: { label: "Students", icon: LucideIcons.GraduationCap },
  Centre: { label: "Centre", icon: LucideIcons.Store },
  "Training & Examination": { label: "Training & Examination", icon: LucideIcons.GraduationCap },
  "Control Panel": {
    label: "Control Panel",
    icon: LucideIcons.SlidersHorizontal,
  },
};

export const StructureLinks: SectionNavLink[] = [
  {
    href: "/structure/category",
    label: "Category",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/structure/theme",
    label: "Theme",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/structure/grade",
    label: "Grade",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/structure/tier",
    label: "Tier",
    visible: ["superadmin", "principal", "masteradmin", "manager"],
  },
];

export const DeUsersLinks: SectionNavLink[] = [
  {
    href: "/deusers/enrolment",
    label: "Enrolments",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
  {
    href: "/deusers/student",
    label: "Students",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
  {
    href: "/deusers/parent",
    label: "Parents",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
];

export const ClassLinks: SectionNavLink[] = [
  {
    href: "/class/attendance",
    label: "Attendance",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/class/manage",
    label: "Manage Classes",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/class",
    label: "Lesson Sequence",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/class/schedule",
    label: "Class Schedule",
    visible: ["superadmin", "masteradmin", "principal"],
  },
  {
    href: "/class/roster",
    label: "Duty Roster",
    // Hidden from every role until the MY008 trial signs off; the page still opens by URL.
    // To show it again: ["superadmin", "masteradmin", "principal", "manager", "teacher"]
    visible: [],
  },
];

export const CertificateLinks: SectionNavLink[] = [
  {
    href: "/certificate/ready",
    label: "Ready to Print",
    visible: ["superadmin", "masteradmin"],
  },
  {
    href: "/certificate/printed",
    label: "Printed",
    visible: ["superadmin", "masteradmin"],
  },
];

export const PromocodeLinks: SectionNavLink[] = [
  {
    href: "/promocode/active",
    label: "Active Code",
    visible: ["superadmin", "masteradmin", "principal"],
  },
  {
    href: "/promocode/inactive",
    label: "Inactive Code",
    visible: ["superadmin", "masteradmin", "principal"],
  },
];

export const DePointsLinks: SectionNavLink[] = [
  {
    href: "/depoints",
    label: "Transactions",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
  {
    href: "/depoints/redemption",
    label: "Redemption",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
  {
    href: "/depoints/category",
    label: "Category",
    visible: ["superadmin", "masteradmin", "principal"],
  },
];

export const MerchandiseLinks: SectionNavLink[] = [
  {
    href: "/merchandise",
    label: "Merchandise",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/merchandise/transfer",
    label: "Stock Order",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/merchandise/stock-movements",
    label: "Stock Movement",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/merchandise/parent-transactions",
    label: "Parent Order",
    visible: ["superadmin", "masteradmin", "principal"],
  },
];

export const REPORT_CATEGORIES: {
  key: ReportCategory;
  label: string;
  icon: LucideIcon;
  accent: "emerald" | "blue" | "amber" | "violet";
}[] = [
  {
    key: "royalty_finance",
    label: "Royalty & Finance",
    icon: LucideIcons.Wallet,
    accent: "emerald",
  },
  {
    key: "students",
    label: "Students & Academic",
    icon: LucideIcons.GraduationCap,
    accent: "blue",
  },
  {
    key: "leads",
    label: "Leads & Marketing",
    icon: LucideIcons.Megaphone,
    accent: "amber",
  },
  {
    key: "operations",
    label: "Operations",
    icon: LucideIcons.CalendarDays,
    accent: "violet",
  },
];

export const ReportLinks: SectionNavLink[] = [
  {
    href: `/report/royalty`,
    label: "Royalty Report",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "royalty_finance",
    description:
      "Centre monthly fees, royalty base and royalty owed per student",
  },
  {
    href: `/report/finance`,
    label: "Finance Report",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "royalty_finance",
    description: "Monthly revenue split by extend, advance and new enrolment",
  },
  {
    href: `/report/centre_progression`,
    label: "Student Growth",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "students",
    description:
      "Centre monthly student movement: enrolments, transfers, active count",
  },
  {
    href: `/report/centre_retention`,
    label: "Grade Retention Report",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "students",
    description:
      "Percentage of students who continue to the next term after completing a term",
  },
  {
    href: `/report/programme_retention`,
    label: "Student Duration Report",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "students",
    description: "Number of grades students study in Deemcee",
  },
  {
    href: `/report/student_attendance`,
    label: "Student Attendance",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "students",
    description: "Weekly attended, absent, freeze and replacement counts for one centre",
  },
  // {
  //   href: `/report/student_enrolment_end_date`,
  //   label: "Enrolment End Dates",
  //   visible: ["superadmin", "principal"],
  // },
  {
    href: `/report/teacher_attendance`,
    label: "Teacher Attendance",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "operations",
    description: "Monthly class attendance counts per teacher",
  },
  {
    href: `/report/video_status`,
    label: "Video Status",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "operations",
    description:
      "Student video submissions: due, submitted and pending with rates",
  },
  {
    href: `/report/invoice`,
    label: "Invoice Summary",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "royalty_finance",
    description:
      "Searchable invoice list with amounts, balances and payment status",
  },
  {
    href: `/report/lead`,
    label: "Lead Report",
    visible: ["superadmin", "masteradmin", "principal"],
    category: "leads",
    description:
      "Centre lead funnel, sources and conversion rate with lead list",
  },

  {
    href: `/report/hq_lead`,
    label: "HQ Lead Report",
    visible: ["superadmin"],
    category: "leads",
    description: "Monthly HQ-source lead counts per branch across all centres",
  },
  {
    href: `/report/master_lead`,
    label: "Lead Master Report",
    visible: ["masteradmin"],
    category: "leads",
    description:
      "Monthly lead counts per branch across a master's sub-branches",
  },

  {
    href: `/report/referral`,
    label: "Referral Report",
    visible: ["superadmin", "masteradmin"],
    category: "leads",
    description: "Monthly enrolment counts by referral and marketing channel",
  },

  {
    href: `/report/hq_centre_progression`,
    label: "HQ Student Growth",
    visible: ["superadmin"],
    category: "students",
    description:
      "Monthly student movement across all centres: enrolments, transfers, active count",
  },

  {
    href: `/report/hq_centre_retention`,
    label: "HQ Grade Retention Report",
    visible: ["superadmin"],
    category: "students",
    description:
      "Percentage of students who continue to the next term after completing a term, across all centres",
  },

  {
    href: `/report/hq_programme_retention`,
    label: "HQ Student Duration Report",
    visible: ["superadmin"],
    category: "students",
    description:
      "Number of grades students study in Deemcee, across all centres",
  },

  {
    href: `/report/hq`,
    label: "HQ Royalty Summary",
    visible: ["superadmin"],
    category: "royalty_finance",
    description: "Royalty owed per branch across all centres (HQ-wide)",
  },

  {
    href: `/report/master`,
    label: "Master Royalty Summary",
    visible: ["masteradmin"],
    category: "royalty_finance",
    description: "Royalty owed per branch across a master's sub-branches",
  },

  {
    href: `/report/royalty-submissions`,
    label: "Royalty Submission Tracker",
    visible: ["superadmin", "masteradmin"],
    category: "royalty_finance",
    description:
      "Track which branches submitted royalty on time, late or pending",
  },

  {
    href: `/report/master_centre_progression`,
    label: "Master Student Growth",
    visible: ["masteradmin"],
    category: "students",
    description: "Student movement and growth across a master's sub-branches",
  },

  {
    href: `/report/master_centre_retention`,
    label: "Master Grade Retention",
    visible: ["masteradmin"],
    category: "students",
    description:
      "Percentage of students who continue to the next term, across a master's sub-branches",
  },

  {
    href: `/report/master_programme_retention`,
    label: "Master Student Duration",
    visible: ["masteradmin"],
    category: "students",
    description:
      "Number of grades students study in Deemcee, across a master's sub-branches",
  },

  // {
  //   href: `/report/finance`,
  //   label: "Finance Report",
  //   visible: ["superadmin", "principal", "manager"],
  // },
  // {
  //   href: `/report/progression`,
  //   label: "Monthly Centre Progression",
  //   visible: ["superadmin", "principal", "manager"],
  // },
  // {
  //   href: `/report/referral`,
  //   label: "Referral Report",
  //   visible: ["superadmin", "principal", "manager"],
  // },
];

export const UserManagementLinks: SectionNavLink[] = [
  {
    href: "/users/superadmin",
    label: "Superadmin",
    visible: ["superadmin"],
  },
  {
    href: "/users/masteradmin",
    label: "Master Admin",
    visible: ["superadmin", "masteradmin"],
  },
  {
    href: "/users/principal",
    label: "Principal",
    visible: ["superadmin", "masteradmin", "principal"],
  },
  {
    href: "/users/manager",
    label: "Manager",
    visible: ["superadmin", "masteradmin", "principal", "manager"],
  },
  {
    href: "/users/teacher",
    label: "Teacher",
    visible: ["superadmin", "masteradmin", "principal", "manager", "teacher"],
  },
];

export const IsSuperadmin = ["superadmin"];

export const IsMasterAdminOrHigher = ["superadmin", "masteradmin"];

export const IsPrincipalOrHigher = ["superadmin", "masteradmin", "principal"];

export const IsManagerOrHigher = [
  "superadmin",
  "masteradmin",
  "principal",
  "manager",
];

export const branchSelectorPermission = [
  "/users/principal",
  "/users/manager",
  "/users/teacher",
  "/calendar",
  "/events",
  "/class",
  "/class/attendance",
  "/class/manage",
  "/class/schedule",
  "/class/roster",
  "/deusers",
  "/deusers/parent",
  "/deusers/student",
  "/deusers/enrolment",
  "/principal",
  "/manager",
  "/teacher",
  "/student",
  "/parent",
  "/certificate/ready",
  "/certificate/printed",
  "/report",
  "/report/royalty",
  "/report/programme_retention",
  // "/report/finance",
  "/report/progression",
  "/report/centre_progression",
  "/report/centre_retention",
  "/report/student_enrolment_end_date",
  "/report/teacher_attendance",
  "/report/hq_centre_progression",
  "/report/hq_centre_retention",
  "/report/master_centre_progression",
  "/report/master_centre_retention",
  "/report/referral",
  "/lead",
  "/open-day",
  "/event-design-requests",
  "/event-proposals",
  "/ticket",
  "/report/invoice",
  "/report/lead",
  "/report/video_status",
  "/video_due",
  "/depoints",
  "/notifications",
  "/centre-evaluations",
  "/teacher-qualifications",
  "/teacher-qualifications/teacher",
  "/teacher-qualifications/reports",
];

export const SERVER_ACTION_STATE = {
  zodErr: null,
  success: null,
  error: null,
  msg: "",
};
// good_user / good_password - Bank of America
export const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const MONTHS = [
  { id: 1, label: "January", value: "1" },
  { id: 2, label: "February", value: "2" },
  { id: 3, label: "March", value: "3" },
  { id: 4, label: "April", value: "4" },
  { id: 5, label: "May", value: "5" },
  { id: 6, label: "June", value: "6" },
  { id: 7, label: "July", value: "7" },
  { id: 8, label: "August", value: "8" },
  { id: 9, label: "September", value: "9" },
  { id: 10, label: "October", value: "10" },
  { id: 11, label: "November", value: "11" },
  { id: 12, label: "December", value: "12" },
];

export const REGIONS = [
  { id: 1, label: "Malaysia", value: "Malaysia" },
  { id: 2, label: "Australia", value: "Australia" },
  { id: 3, label: "Singapore", value: "Singapore" },
  { id: 4, label: "Thailand", value: "Thailand" },
  { id: 5, label: "Philippines", value: "Philippines" },
];

export const BRANCH_GRADE = [
  { id: 1, label: "Level 1 (20%)", value: "1" },
  { id: 2, label: "Level 2 (15%)", value: "2" },
];

export const CLASS_TYPES = [
  { value: "Kiddo", label: "Kiddo" },
  { value: "Kids", label: "Kids" },
  { value: "Superkids", label: "Superkids" },
];

export const GRADE = [
  { id: 1, label: "Grade 1" },
  { id: 2, label: "Grade 2" },
  { id: 3, label: "Grade 3" },
  { id: 4, label: "Grade 4" },
  { id: 5, label: "Grade 5" },
  { id: 6, label: "Grade 6" },
];

export const DAYS = [
  { id: 1, label: "Monday", value: "Monday" },
  { id: 2, label: "Tuesday", value: "Tuesday" },
  { id: 3, label: "Wednesday", value: "Wednesday" },
  { id: 4, label: "Thursday", value: "Thursday" },
  { id: 5, label: "Friday", value: "Friday" },
  { id: 6, label: "Saturday", value: "Saturday" },
  { id: 7, label: "Sunday", value: "Sunday" },
];

export const StarterKitItems = [
  { label: "Student Booklet - G1", value: "14" },
  { label: "Student Booklet - G2", value: "15" },
  { label: "Student Booklet - G3", value: "16" },
  { label: "Student Booklet - G4", value: "17" },
  { label: "Student Booklet - G5", value: "18" },
  { label: "Student Booklet - G6", value: "19" },
  { label: "Student Uniform | Male - 36", value: "221" },
  { label: "Student Uniform | Male - 38", value: "222" },
  { label: "Student Uniform | Female - 36", value: "223" },
  { label: "Student Uniform | Female - 38", value: "225" },
];

export const ReferralChannels = [
  "Facebook",
  "Google Form",
  "Website",
  "Referral",
  "Others",
  "Whatsapp",
  "Walk In",
];

export const STUDENT_FILTERTING_STATUSES = [
  { value: "IN_PROGRESS", label: "IN PROGRESS" },
  { value: "PENDING", label: "PENDING" },
  { value: "DROPPED_OUT", label: "DROPPED_OUT" },
  { value: "GRADUATED", label: "GRADUATED" },
];

export const ENROLMENT_STATUS_CHOICES = [
  { value: "IN_PROGRESS", label: "IN PROGRESS" },
  // { value: "PENDING", label: "PENDING" },
  // { value: "COMPLETED", label: "COMPLETED" },
];

export const AttendanceStatus = [
  {
    value: "ATTENDED",
    label: "Attended",
    className: "bg-gray-300 text-white hover:bg-green-600",
    isActive: "bg-green-600",
  },
  {
    value: "ABSENT",
    label: "Absent",
    className: "bg-gray-300 text-white hover:bg-red-600",
    isActive: "bg-red-600",
  },
  {
    value: "FREEZED",
    label: "P-Freeze",
    className: "bg-gray-300 text-white hover:bg-sky-600",
    isActive: "bg-sky-600",
  },
  {
    value: "SFREEZED",
    label: "S-Freeze",
    className: "bg-gray-300 text-white hover:bg-amber-600",
    isActive: "bg-amber-600",
  },
  {
    value: "REPLACEMENT",
    label: "Replacement",
    className: "bg-gray-300 text-white hover:bg-purple-600",
    isActive: "bg-purple-600",
  },
];

export const ReplacementAttendanceStatus = [
  {
    value: "ATTENDED",
    label: "Attended",
    className: "bg-gray-300 text-white hover:bg-green-600",
    isActive: "bg-green-600",
    isDisabled: false,
  },
  {
    value: "ABSENT",
    label: "Absent",
    className: "bg-gray-300 text-white hover:bg-red-600",
    isActive: "bg-red-600",
    isDisabled: false,
  },
  {
    value: "PENDING",
    label: "Pending",
    className: "bg-gray-300 text-white hover:bg-yellow-600",
    isActive: "bg-yellow-600",
    isDisabled: true,
  },
];

export const PROMO_TYPE = [
  { id: 1, value: "ENROLMENT", label: "Enrolment" },
  { id: 2, value: "MERCHANDISE", label: "Merchandise" },
  { id: 3, value: "OTHER", label: "Other" },
];

export const FINANCE_REPORT_TYPE = [
  { id: 1, label: "Cumulative", value: "Cumulative" },
  { id: 2, label: "Monthly", value: "Monthly" },
];

export const STUDENT_GRADE = [
  { value: 1, label: "Grade 1" },
  { value: 2, label: "Grade 2" },
  { value: 3, label: "Grade 3" },
  { value: 4, label: "Grade 4" },
  { value: 5, label: "Grade 5" },
  { value: 6, label: "Grade 6" },
];

export const STUDENT_ENROLMENT_FEE_STATUSES = [
  { value: "PAID", label: "PAID" },
  { value: "PENDING", label: "PENDING_PAYMENT" },
];

export const STUDENT_ENROLMENT_SORTBY = [
  { value: "name", label: "Name" },
  { value: "start_date", label: "Start Date" },
  { value: "end_date", label: "End Date" },
];

export const SOURCE_CHOICES_BRANCH = [
  { value: "facebook_own", label: "Facebook Own" },
  { value: "google_form", label: "Google Form" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "others", label: "Others" },
  { value: "whatsapp_direct", label: "Direct WhatsApp" },
  { value: "walk_in", label: "Walk In" },
];

export const SOURCE_CHOICES_HQ = [
  { value: "facebook_hq", label: "Facebook HQ" },
  { value: "google_form", label: "Google Form" },
  { value: "website", label: "Website" },
  { value: "whatsapp_hq", label: "WhatsApp Hq" },
  { value: "others", label: "Others" },
];

export const SOURCE_CHOICES_ALL = [
  { value: "facebook_hq", label: "Facebook HQ" },
  { value: "facebook_own", label: "Facebook Own" },
  { value: "google_form", label: "Google Form" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "others", label: "Others" },
  { value: "whatsapp_direct", label: "Direct WhatsApp" },
  { value: "whatsapp_hq", label: "WhatsApp Hq" },
  { value: "walk_in", label: "Walk In" },
];

export const ADS_CATEGORY_CHOICES = [
  { value: "bm", label: "BM" },
  { value: "cn", label: "CN" },
  { value: "en", label: "EN" },
  { value: "th", label: "TH" },
  { value: "not_set", label: "Not Set" },
];

export const ADS_CATEGORY_CHOICES_CU = [
  { value: "bm", label: "BM" },
  { value: "cn", label: "CN" },
  { value: "en", label: "EN" },
  { value: "th", label: "TH" },
];

export const ACTION_CHOICES = [
  { value: "not_contacted", label: "Not Contacted" },
  { value: "attempted_contact", label: "Attempted Contact" },
  { value: "to_confirm_preview", label: "To confirm preview" },
  { value: "preview_confirmed", label: "Preview confirmed" },
  { value: "follow_up", label: "Follow up" },
  { value: "refer_to_others", label: "Refer to others" },
  { value: "not_interested", label: "Not Interested" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "duplicate", label: "Duplicate Entry" },
  { value: "too_young", label: "Too Young" },
  { value: "kiv", label: "KIV" },
  { value: "others", label: "Others" },
];

export const RESULT_CHOICES_ALL = [
  { value: "new", label: "-" },
  { value: "no_enrol", label: "No enrol" },
  { value: "follow_up", label: "Follow up" },
  { value: "rejected", label: "Rejected lead" },
  { value: "awaiting_register", label: "Awaiting Register" },
  { value: "registered", label: "Registered" },
  { value: "completed", label: "Enrolled" },
];

export const RESULT_CHOICES = [
  { value: "new", label: "-" },
  { value: "no_enrol", label: "No enrol" },
  { value: "follow_up", label: "Follow up" },
  { value: "awaiting_register", label: "Awaiting Register" },
  { value: "rejected", label: "Rejected lead" },
];

export const RESULT_CHOICES_LEAD_REGISTERED = [
  { value: "registered", label: "Registered" },
  { value: "completed", label: "Enrolled" },
];

export const TICKET_CATEGORY_CHOICES = [
  { value: "bug", label: "Error / Mistake Correction" },
  { value: "improvement", label: "Improvement / Enhancement" },
  { value: "new_features_request", label: "New Feature Request" },
  { value: "general_inquiry", label: "General Inquiry" },
];

export const TICKET_STATUS_CHOICES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending_reply", label: "Pending Reply" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export const TICKET_PRIORITY_CHOICES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const REWARD_CATEGORY_CHOICES = [
  { value: "plushie", label: "Plushie" },
  { value: "poster", label: "Poster" },
  { value: "uniform", label: "Uniform" },
  { value: "brochure", label: "Brochure" },
  { value: "event_asset", label: "Event Asset" },
  { value: "tag", label: "Tag" },
  { value: "bunting", label: "Bunting" },
  { value: "other", label: "Other" },
];

export const NOTIFICATION_PRIORITY_CHOICES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const NOTIFICATION_TARGET_ROLES = [
  { value: "masteradmin", label: "Master Admin" },
  { value: "principal", label: "Principal" },
  { value: "manager", label: "Manager" },
  { value: "teacher", label: "Teacher" },
  { value: "parent", label: "Parent" },
];

export const STORAGE_KEY_FCM_TOKEN = "fcmToken";
export const STORAGE_KEY_NOTIFICATION_PROMPT_DISMISSED =
  "notificationPromptDismissed";

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
export const NOTIFICATION_PROMPT_DELAY_MS = 3000;
export const PWA_PROMPT_DELAY_MS = 2000;

// Device Types
export const DEVICE_TYPE = {
  WEB: "web",
  ANDROID: "android",
  IOS: "ios",
  WINDOWS: "windows",
  MAC: "mac",
  LINUX: "linux",
} as const;

// Browser Names
export const BROWSER_NAME = {
  CHROME: "Chrome",
  FIREFOX: "Firefox",
  SAFARI: "Safari",
  EDGE: "Edge",
  UNKNOWN: "Unknown Browser",
} as const;

// User Agent Patterns
export const UA_PATTERNS = {
  ANDROID: /Android/i,
  IOS: /iPhone|iPad|iPod/i,
  WINDOWS: /Windows/i,
  MAC: /Mac/i,
  LINUX: /Linux/i,
  CHROME: /Chrome/i,
  FIREFOX: /Firefox/i,
  SAFARI: /Safari/i,
  EDGE: /Edg/i,
  MS_STREAM: /MSStream/i,
} as const;
